import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/App';
import { Camera, FileText, Users, Calendar, ArrowRight, Check } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LandingPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, login } = useAuth();
  const [carouselImages, setCarouselImages] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [content, setContent] = useState(null);

  useEffect(() => {
    loadCarouselImages();
    loadContent();
  }, []);

  useEffect(() => {
    if (carouselImages.length > 0) {
      const timer = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % carouselImages.length);
      }, 5000);
      return () => clearInterval(timer);
    }
  }, [carouselImages]);

  const loadCarouselImages = async () => {
    try {
      const res = await axios.get(`${API}/carousel-images`);
      setCarouselImages(res.data);
    } catch (error) {
      console.error('Error loading carousel');
    }
  };

  const loadContent = async () => {
    try {
      const res = await axios.get(`${API}/landing-content`);
      setContent(res.data);
    } catch (error) {
      console.error('Error loading content:', error);
      // Set default content in case of error
      setContent({
        hero_title: "Capturez l'émotion de vos événements",
        hero_subtitle: "Photo & Vidéo professionnelle pour mariages, Bar/Bat Mitzvah et événements inoubliables",
        hero_button_primary: "Obtenir un devis",
        hero_button_secondary: "Nos services",
        services_title: "Nos prestations",
        services: [
          { title: "Mariages", description: "Couverture complète de votre journée" },
          { title: "Bar/Bat Mitsvahs", description: "Synagogue et réception" },
          { title: "Brit Milas", description: "Un moment précieux capturé" },
          { title: "Hennés", description: "Toute la fête en images" }
        ],
        features_title: "Ce qui nous distingue",
        features: [
          "Film version longue + Teaser inclus",
          "Livraison sur clé USB personnalisée",
          "Photographes et vidéastes professionnels",
          "Option Magnets pour vos invités",
          "Prise de vue drone disponible",
          "Couverture intégrale de l'event"
        ],
        cta_title: "Prêt à créer des souvenirs inoubliables ?",
        cta_subtitle: "Remplissez notre formulaire et recevez votre devis personnalisé.",
        cta_button: "Obtenir mon devis gratuit",
        footer_text: "Kolnoa Production"
      });
    }
  };

  if (!content) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin h-12 w-12 border-4 border-amber-600 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-stone-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Header */}
      <header className="bg-white border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_wedding-erp/artifacts/6y9ywpy7_image.png" 
              alt="Kolnoa" 
              className="h-12"
            />
            <div className="flex flex-col gap-1">
              <div className="text-stone-600 text-sm font-medium">
                📞 06.68.11.93.43
              </div>
              <a 
                href="https://www.instagram.com/kolnoa.prod/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-xs text-stone-500 hover:text-amber-600 transition-colors"
              >
                <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
                @kolnoa.prod
              </a>
            </div>
          </div>
          <nav className="flex items-center gap-4">
            {user ? (
              <Link to="/backoffice">
                <Button className="bg-amber-600 hover:bg-amber-700 text-white btn-hover" data-testid="backoffice-btn">
                  Login
                </Button>
              </Link>
            ) : (
              <Button onClick={login} className="bg-amber-600 hover:bg-amber-700 text-white btn-hover" data-testid="login-btn">
                Login
              </Button>
            )}
          </nav>
        </div>
      </header>

      {/* Hero Section with Carousel */}
      <section className="relative pt-32 pb-20 px-6 overflow-hidden">
        {/* Carousel Background */}
        <div className="absolute inset-0 z-0">
          {carouselImages.length > 0 ? (
            carouselImages.map((img, index) => (
              <div
                key={img.id}
                className={`absolute inset-0 transition-opacity duration-1000 ${
                  index === currentSlide ? 'opacity-100' : 'opacity-0'
                }`}
              >
                <img
                  src={img.url}
                  alt={`Slide ${index + 1}`}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-br from-stone-900/80 via-stone-800/70 to-amber-900/80"></div>
              </div>
            ))
          ) : (
            <div className="absolute inset-0 bg-gradient-to-br from-stone-900 via-stone-800 to-amber-900"></div>
          )}
        </div>

        {/* Content */}
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              {content.hero_title}
            </h1>
            <p className="text-base sm:text-lg text-stone-300 mb-8">
              {content.hero_subtitle}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="bg-amber-600 hover:bg-amber-700 text-white btn-hover">
                <Link to="/questionnaire">
                  {content.hero_button_primary}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="bg-white/10 text-white border-white/20 hover:bg-white/20 btn-hover">
                <a href="#services">{content.hero_button_secondary}</a>
              </Button>
            </div>
          </div>
        </div>

        {/* Carousel Indicators */}
        {carouselImages.length > 1 && (
          <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex gap-2 z-10">
            {carouselImages.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`w-2 h-2 rounded-full transition-all ${
                  index === currentSlide ? 'bg-amber-500 w-8' : 'bg-white/50'
                }`}
              />
            ))}
          </div>
        )}
      </section>

      {/* Services */}
      <section className="py-16 px-6 bg-white" id="services">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-display text-3xl font-bold text-stone-800 text-center mb-12">
            {content.services_title}
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {content.services.map((service, i) => {
              const icons = [Camera, Users, Calendar, FileText];
              const Icon = icons[i % icons.length];
              return (
                <div key={i} className="p-6 bg-stone-50 rounded-xl card-hover" data-testid={`service-card-${i}`}>
                  <Icon className="h-10 w-10 text-amber-600 mb-4" />
                  <h3 className="font-semibold text-lg text-stone-800 mb-2">{service.title}</h3>
                  <p className="text-stone-600">{service.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-display text-3xl font-bold text-stone-800 text-center mb-12">
            {content.features_title}
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {content.features.map((feature, i) => (
              <div key={i} className="flex items-center gap-3 p-4 bg-white rounded-lg shadow-sm">
                <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Check className="h-4 w-4 text-amber-600" />
                </div>
                <span className="text-stone-700">{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-6 bg-stone-800">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-display text-3xl font-bold text-white mb-6">
            {content.cta_title}
          </h2>
          <p className="text-stone-300 mb-8">
            {content.cta_subtitle}
          </p>
          <Link to="/questionnaire">
            <Button size="lg" className="bg-amber-500 hover:bg-amber-600 text-white px-8 btn-hover" data-testid="cta-devis-btn">
              {content.cta_button}
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 bg-stone-900 text-stone-400">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-3">
            <img src="/logo-kolnoa.svg" alt="Kolnoa" className="h-8 w-auto" />
            <span className="text-sm text-stone-500">Production</span>
          </div>
          <a 
            href="https://www.instagram.com/kolnoa.prod/" 
            target="_blank" 
            rel="noopener noreferrer"
            className="flex items-center gap-2 hover:text-amber-400 transition-colors"
          >
            <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
            </svg>
            <span className="text-sm">@kolnoa.prod</span>
          </a>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
